import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { VoiceModal } from '@/components/Voice/VoiceModal/VoiceModal';
import { useAllVoices } from '@/hooks/useAllVoices';
import CloneVoiceImg from '@/images/CloneVoice.svg';
import FemaleImg from '@/images/FaceFemale.svg';
import MaleImg from '@/images/FaceMale.svg';
import PlayPreview from '@/images/PlayPreview.svg';
import { ttsVoiceModal } from '@/state/presenters';
import { IVoice, Providers } from '@d-id/types';
import styled from '@emotion/styled';
import { CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRecoilState } from 'recoil';

const VoiceButton = styled.button`
    outline: none;
    background: #fff;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 2rem;
    padding: 5px 8px;
    cursor: pointer;
    transition: background 0.2s;
    display: flex;
    align-items: center;

    :hover {
        background: ${({ theme }) => theme.colors.black[100]};
        transition: background 0.2s;
    }

    &:disabled {
        cursor: not-allowed;
        pointer-events: none;
        color: ${({ theme }) => theme.colors.black[500]};
    }
`;

const ResetVoiceButton = styled.button`
    outline: none;
    cursor: pointer;
    background: transparent;
    width: 100%;
    border-radius: 10px;
    min-height: 7.4rem;
    display: flex;
    align-items: center;
    gap: 1.6rem;
    border: 1.5px solid ${({ theme }) => theme.colors.black[300]};
    padding: 2rem;

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[50]};
        border: 1.5px solid ${({ theme }) => theme.colors.secondary.main};
    }
`;

const Divider = styled.div`
    width: 2px;
    height: 23px;
    border-radius: 5000px;
    background-color: ${({ theme }) => theme.colors.black[300]};
`;

const LeadingIcon = styled(MaskedIcon)`
    margin-right: 0.8rem;
`;

const HeaderButton = styled.button`
    border: none;
    box-sizing: border-box;
    padding: 0;
    color: inherit;

    :disabled {
        opacity: 0.5;
    }
`;

const VoicePlayerButton = styled(HeaderButton)`
    background-color: transparent;
    cursor: pointer;
    width: 32px;
    height: 32px;
    outline: none;

    :disabled {
        cursor: default;
    }
`;

const ImageWrapper = styled.div`
    height: 32px;
    width: 32px;
    background-color: ${({ theme }) => theme.colors.black.main}10;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ListenImage = styled.img`
    height: 2rem;
    width: 2rem;
`;

const ButtonLoaderWrapper = styled.div`
    height: 100%;
    width: 10rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledCircularProgress = styled(CircularProgress)`
    color: ${({ theme }) => theme.colors.black[500]};
`;

function getIconSrc(voice?: IVoice | null) {
    switch (voice?.gender?.toLowerCase()) {
        case 'male':
            return MaleImg;
        case 'female':
            return FemaleImg;
        default:
            return CloneVoiceImg;
    }
}

function TranslationVoiceSelect() {
    const { watch, setValue } = useFormContext();
    const [voiceModalState, setVoiceModalState] = useRecoilState(ttsVoiceModal);
    const { data: voices, isFetching: voicesFetching } = useAllVoices();
    const [voiceModalButtonClicked, setVoiceModalButtonClicked] = useState(false);

    const [selectedVoiceId, selectedTranslationId] = watch(['selectedVoiceId', 'selectedTranslationId']);

    const voicesInitialAwaiting = voicesFetching && !voices?.length;
    const selectedVoice = voices?.find(voice => voice.id === selectedVoiceId) || null;
    const formInitialized = !!selectedTranslationId;

    const buttonLoading = voicesInitialAwaiting && (voiceModalButtonClicked || !!selectedVoiceId);

    useEffect(() => {
        if (voiceModalButtonClicked && !voicesInitialAwaiting) {
            setVoiceModalState(true);
            setVoiceModalButtonClicked(false);
        }
    }, [voiceModalButtonClicked, voicesInitialAwaiting]);

    const handleVoiceButtonClick = async () => {
        setVoiceModalButtonClicked(true);
    };

    const handleResetVoice = () => {
        setValue('selectedVoiceId', undefined, { shouldDirty: true });
        setVoiceModalState(false);
    };

    const originalVoiceRow = (
        <ResetVoiceButton onClick={handleResetVoice}>
            {/* TODO: implement preview */}
            <VoicePlayerButton disabled>
                <ImageWrapper>
                    <ListenImage src={PlayPreview} />
                </ImageWrapper>
            </VoicePlayerButton>
            <Divider />
            <MaskedIcon src={CloneVoiceImg} size="16px" />
            <Typography variant="body1" fontWeight={500}>
                Original voice
            </Typography>
        </ResetVoiceButton>
    );

    if (!formInitialized) {
        return null;
    }

    return (
        <>
            <VoiceButton data-testid="translation-select-voice-button" onClick={handleVoiceButtonClick} disabled={buttonLoading}>
                {buttonLoading ? (
                    <ButtonLoaderWrapper>
                        <StyledCircularProgress color="inherit" size={16} />
                    </ButtonLoaderWrapper>
                ) : (
                    <>
                        <LeadingIcon src={getIconSrc(selectedVoice)} size="16px" />
                        {selectedVoice?.name || 'Cloned voice'}
                    </>
                )}
            </VoiceButton>
            <VoiceModal
                open={voiceModalState}
                onClose={() => setVoiceModalState(false)}
                filters={{ providers: [Providers.Elevenlabs] }}
                selectVoicePinnedContent={originalVoiceRow}
                selectedVoice={selectedVoice}
                onSelectedVoiceChange={(voice: IVoice) => {
                    setValue('selectedVoiceId', voice.id, { shouldDirty: true });
                }}
            />
        </>
    );
}

export default TranslationVoiceSelect;
